import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { LoaderCircle } from "lucide-react";
import { contentQueries, useEditContentMutation } from "@/api/longform";
import { useToast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { LoadingButton } from "@/components/LoadingButton";

interface EditContentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contentId: string;
  personaId: string;
}

const EditContentDialog = ({
  open,
  onOpenChange,
  contentId,
  personaId,
}: EditContentDialogProps) => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const { toast } = useToast();

  const { data: contentData, isLoading: isContentLoading } = useQuery({
    ...contentQueries.contentList(personaId),
    select: (data) =>
      data.data.find((content) => content.content_id === contentId),
  });

  useEffect(() => {
    if (!contentData) return;

    setTitle(contentData.title);
    setContent(contentData.content);
  }, [contentData]);

  const editContentMutation = useEditContentMutation();

  const handleSave = async () => {
    if (!title.trim()) {
      toast({ description: "Title cannot be empty." });
      return;
    }

    editContentMutation.mutate(
      {
        personaId,
        contentId,
        content: { title, content },
      },
      {
        onSuccess: () => {
          toast({ description: "Content updated successfully!" });
          onOpenChange(false);
        },
        onError: () =>
          toast({
            description: "Failed to update content. Please try again.",
            variant: "destructive",
          }),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Content</DialogTitle>
        </DialogHeader>

        {isContentLoading ? (
          <div className="flex justify-center py-8">
            <LoaderCircle className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter content title"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Content</Label>
              <Textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Enter content body"
                className="min-h-[50vh] font-mono"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={editContentMutation.isPending}
          >
            Cancel
          </Button>
          <LoadingButton
            loading={editContentMutation.isPending || isContentLoading}
            onClick={handleSave}
          >
            Save Changes
          </LoadingButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditContentDialog;
